/**
 * @fileoverview Constants and configuration for the Hydraulic Balancing Testing simulation
 *
 * Next-generation educational hydraulic balancing simulation:
 * - Fixed 3 floors x 3 rooms building (9 rooms)
 * - Coupled hydraulic network (pump curve + shared risers + parallel branches)
 * - Realistic TRV preset positions with Kv table (Danfoss RA-N style insert)
 * - Weather-compensated heating curve, energy metering, scoring
 *
 * @module HydraulicBalancingTesting/constants
 */

// ============================================================================
// PHYSICAL CONSTANTS
// ============================================================================

export const WATER = {
  /** Density at ~45°C [kg/m³] */
  DENSITY: 990,
  /** Specific heat capacity [J/(kg·K)] */
  SPECIFIC_HEAT: 4186,
};

export const AIR = {
  /** Volumetric heat capacity [Wh/(m³·K)] */
  VOLUMETRIC_HEAT_CAPACITY: 0.34,
};

// ============================================================================
// BUILDING GEOMETRY (3 floors x 3 columns, fixed)
// ============================================================================

export const BUILDING = {
  FLOORS: 3,
  COLUMNS: 3,
  /** Room footprint [m] */
  ROOM_WIDTH: 5,
  ROOM_DEPTH: 4,
  ROOM_HEIGHT: 2.8,
  /** Storey height (pipe riser length per floor) [m] */
  FLOOR_HEIGHT: 3.0,
  /** U-value between rooms [W/(m²·K)] */
  U_INTERNAL: 0.6,
  /** Roof U-value relative to wall U-value */
  ROOF_U_FACTOR: 0.8,
  /** Ground floor slab U-value relative to wall U-value */
  GROUND_U_FACTOR: 0.6,
  /** Minimum effective ground temperature [°C] */
  GROUND_MIN_TEMP: 8,
};

/** Derived areas [m²] */
export const AREAS = {
  WALL_FRONT_BACK: BUILDING.ROOM_WIDTH * BUILDING.ROOM_HEIGHT,   // 14 (north & south, always exterior)
  WALL_SIDE: BUILDING.ROOM_DEPTH * BUILDING.ROOM_HEIGHT,         // 11.2 (east/west)
  FLOOR_CEILING: BUILDING.ROOM_WIDTH * BUILDING.ROOM_DEPTH,      // 20
};

export const ROOM_VOLUME = BUILDING.ROOM_WIDTH * BUILDING.ROOM_DEPTH * BUILDING.ROOM_HEIGHT;

/** Thermal capacitance per room (air + furniture + partition mass) [J/K] */
export const ROOM_THERMAL_CAPACITANCE = 1.2e7;

/**
 * Room layout: floor 0 = ground floor. Setpoints follow DIN EN 12831 customary
 * values (living 20°C, bathroom 24°C, bedroom 18°C).
 */
export const ROOM_LAYOUT = [
  // floor 0 (ground)
  [
    { name: 'Living Room', Tset: 20, Uout: 0.5 },
    { name: 'Kitchen', Tset: 20, Uout: 0.5 },
    { name: 'Office', Tset: 20, Uout: 0.6 },
  ],
  // floor 1
  [
    { name: 'Bedroom', Tset: 18, Uout: 0.5 },
    { name: 'Bathroom', Tset: 24, Uout: 0.5 },
    { name: "Child's Room", Tset: 21, Uout: 0.5 },
  ],
  // floor 2 (top, under the roof)
  [
    { name: 'Guest Room', Tset: 18, Uout: 0.6 },
    { name: 'Hobby Room', Tset: 20, Uout: 0.6 },
    { name: 'Studio', Tset: 20, Uout: 0.7 },
  ],
];

// ============================================================================
// VALVE PRESETS (thermostatic valve insert, Danfoss RA-N 15 style)
// ============================================================================

/**
 * Preset positions and their Kv values [m³/h at 1 bar] with the TRV head
 * fully open. Position "N" = no presetting (fully open insert).
 */
export const PRESET_POSITIONS = [
  { label: '1', kv: 0.04 },
  { label: '1.5', kv: 0.06 },
  { label: '2', kv: 0.08 },
  { label: '2.5', kv: 0.10 },
  { label: '3', kv: 0.12 },
  { label: '3.5', kv: 0.16 },
  { label: '4', kv: 0.20 },
  { label: '4.5', kv: 0.26 },
  { label: '5', kv: 0.32 },
  { label: '5.5', kv: 0.38 },
  { label: '6', kv: 0.45 },
  { label: '6.5', kv: 0.50 },
  { label: '7', kv: 0.56 },
  { label: 'N', kv: 0.73 },
];

/** Index of the fully open ("N") preset position */
export const PRESET_MAX_INDEX = PRESET_POSITIONS.length - 1;

/** TRV proportional band [K] (2K band centred on the setpoint) */
export const TRV_P_BAND = 2.0;

// ============================================================================
// RADIATORS (EN 442)
// ============================================================================

export const RADIATOR = {
  /** Radiator exponent (EN 442) */
  EXPONENT: 1.3,
  /** Nominal conditions: ΔT = 50 K (75/65/20) */
  NOMINAL_DT: 50,
  /** Selectable nominal outputs at ΔT50 [W] */
  SIZES: [400, 600, 800, 1000, 1200, 1500, 1800, 2200, 2600, 3000, 3500, 4000],
  /** Internal hydraulic resistance of radiator + lockshield as Kv [m³/h] */
  BODY_KV: 1.8,
  /** Iterations for return-temperature equilibrium */
  ITERATIONS: 6,
};

// ============================================================================
// PIPE NETWORK
// ============================================================================

export const PIPES = {
  /** Darcy friction factor (kept constant for transparency/stability) */
  FRICTION_FACTOR: 0.03,
  /** Fitting allowance (equivalent length multiplier) */
  FITTING_FACTOR: 1.3,
  /** Riser inner diameter [m] */
  RISER_DIAMETER: 0.025,
  /** Floor distribution pipe inner diameter [m] */
  FLOOR_DIAMETER: 0.016,
  /** Branch (room connection) pipe inner diameter [m] */
  BRANCH_DIAMETER: 0.013,
  /** One-way riser length per floor [m] */
  RISER_LENGTH_PER_FLOOR: BUILDING.FLOOR_HEIGHT,
  /** One-way horizontal length per column step [m] */
  FLOOR_LENGTH_PER_COLUMN: 4.0,
  /** One-way branch length to each radiator [m] */
  BRANCH_LENGTH: 2.0,
  /** Base circuit length (heat pump to riser foot, one-way) [m] */
  BASE_LENGTH: 4.0,
  /** Base circuit diameter [m] */
  BASE_DIAMETER: 0.025,
  /** Flow velocity above which valves start making noise [m/s] */
  NOISE_VELOCITY: 0.45,
};

/**
 * Selectable standard inner pipe diameters [m] (labelled in mm). Includes the
 * default tier diameters (0.013 / 0.016 / 0.025 m) so the Selects always show
 * the active value. Used by the adjustable pipe-network controls.
 */
export const PIPE_DIAMETER_OPTIONS = [
  { value: 0.010, label: '10 mm' },
  { value: 0.012, label: '12 mm' },
  { value: 0.013, label: '13 mm' },
  { value: 0.016, label: '16 mm' },
  { value: 0.020, label: '20 mm' },
  { value: 0.025, label: '25 mm' },
  { value: 0.032, label: '32 mm' },
  { value: 0.040, label: '40 mm' },
];

// ============================================================================
// CIRCULATION PUMP
// ============================================================================

export const PUMP = {
  /** Maximum head setting [kPa] */
  MAX_HEAD_KPA: 60,
  /** Minimum head setting [kPa] */
  MIN_HEAD_KPA: 10,
  /** Default head setting [kPa] */
  DEFAULT_HEAD_KPA: 40,
  /** Max volume flow at maximum head setting [m³/h] */
  MAX_FLOW_AT_MAX_HEAD: 4.0,
  /** Overall pump efficiency (hydraulic/electric) */
  EFFICIENCY: 0.25,
};

// ============================================================================
// HEAT PUMP
// ============================================================================

export const HEAT_PUMP = {
  COMPRESSOR_EFFICIENCY: 0.55,
  EVAPORATOR_APPROACH: 6,
  CONDENSER_APPROACH: 3,
  MIN_SUPPLY: 25,
  MAX_SUPPLY: 60,
  DEFAULT_SUPPLY: 45,
  /** Minimum circulation flow required by the heat pump [m³/h] */
  MIN_FLOW_M3H: 0.35,
  /** Design conditions */
  DESIGN_SUPPLY: 45,
  DESIGN_SPREAD: 5,
  /** Heating curve defaults: Tsup = 20 + offset + slope·(20 − Toutdoor) */
  CURVE_SLOPE_DEFAULT: 0.83,
  CURVE_SLOPE_MIN: 0.2,
  CURVE_SLOPE_MAX: 1.4,
  CURVE_OFFSET_DEFAULT: 0,
  CURVE_OFFSET_MIN: -5,
  CURVE_OFFSET_MAX: 10,
};

export const OUTDOOR = {
  DEFAULT: 0,
  MIN: -20,
  MAX: 20,
  /** Design outdoor temperature (DIN EN 12831) [°C] */
  DESIGN: -10,
};

/** Default ventilation air change rate [1/h] */
export const DEFAULT_ACH = 0.5;

// ============================================================================
// SIMULATION TIMING
// ============================================================================

export const SIM = {
  /** Real-time tick interval [ms] */
  TICK_MS: 200,
  /** Simulated seconds per tick at 1x speed */
  BASE_DT: 60,
  /** Selectable speed multipliers */
  SPEEDS: [1, 5, 20],
  /** History samples kept per room (for sparklines) */
  HISTORY_LENGTH: 120,
};

// ============================================================================
// SCORING / EVALUATION
// ============================================================================

export const SCORING = {
  /** Flow deviation regarded as balanced [fraction of design flow] */
  BALANCE_TOLERANCE: 0.10,
  /** Looser tolerance used by the service-call mission */
  BALANCE_TOLERANCE_LOOSE: 0.15,
  /** Comfort tolerance [K] */
  COMFORT_TOLERANCE: 1.0,
  /** Valve authority recommended minimum */
  MIN_AUTHORITY: 0.3,
};

// ============================================================================
// SVG VISUALIZATION
// ============================================================================

export const SVG_DIM = {
  WIDTH: 820,
  HEIGHT: 700,
  ROOM_WIDTH: 196,
  ROOM_HEIGHT: 148,
  /** Left margin where the risers run */
  MARGIN_LEFT: 130,
  GROUND_Y: 620,
};

export const COLORS = {
  SUPPLY: '#e2574c',
  SUPPLY_DIM: 'rgba(226, 87, 76, 0.45)',
  RETURN: '#4682b4',
  RETURN_DIM: 'rgba(70, 130, 180, 0.45)',
  HOT: '#ff4d4f',
  WARM: '#faad14',
  GOOD: '#52c41a',
  COLD: '#1890ff',
  PRIMARY: '#0050b3',
  SECONDARY: '#1890ff',
  GRAY: 'rgba(0, 0, 0, 0.45)',
  BORDER: '#d9d9d9',
  WALL: '#0B75BD',
  /** Diverging, colour-blind-safe palette for flow deviation */
  DEV_UNDER_STRONG: '#2166ac',
  DEV_UNDER: '#67a9cf',
  DEV_OK: '#1b9e77',
  DEV_OVER: '#ef8a62',
  DEV_OVER_STRONG: '#b2182b',
};

// ============================================================================
// DEFAULT STATE FACTORIES
// ============================================================================

/**
 * Creates the default state object for one room.
 * @param {number} floor - Floor index (0 = ground)
 * @param {number} col - Column index (0 = next to riser)
 */
export const createRoom = (floor, col) => {
  const layout = ROOM_LAYOUT[floor][col];
  return {
    id: floor * BUILDING.COLUMNS + col + 1,
    floor,
    col,
    name: layout.name,
    /** Comfort setpoint [°C] */
    Tset: layout.Tset,
    /** Envelope U-value to outside [W/(m²·K)] */
    Uout: layout.Uout,
    /** Preset position index into PRESET_POSITIONS */
    presetIndex: PRESET_MAX_INDEX,
    /** Radiator nominal output at ΔT50 [W] (auto-sized on init) */
    radiatorNominal: 1000,
    /** Current room temperature [°C] */
    T: 17,
    /** Temperature history for sparkline */
    history: [17],
    /** TRV opening fraction (0-1) */
    trv: 1,
    /** Last computed values (filled by the engine) */
    flow: 0,          // m³/s
    designLoad: 0,    // W
    designFlow: 0,    // m³/s
    Treturn: 20,
    qrad: 0,
    losses: null,
    authority: 0,
    velocity: 0,
  };
};

/** Creates the 9-room building as a flat array (ordered floor-major). */
export const createBuilding = () => {
  const rooms = [];
  for (let f = 0; f < BUILDING.FLOORS; f++) {
    for (let c = 0; c < BUILDING.COLUMNS; c++) {
      rooms.push(createRoom(f, c));
    }
  }
  return rooms;
};

/**
 * Builds the full per-segment pipe-network geometry (each segment its own
 * length/diameter), defaulting to the PIPES catalogue. Layout:
 *  - base: heat pump → riser foot
 *  - risers[f]: riser segment leading up to floor f's tee (foot→f0 = risers[0])
 *  - floors[f][s]: floor-f distribution run from column s to s+1
 *  - branches[idx]: connection to each room (flat floor-major index)
 */
export const createDefaultPipes = () => {
  const seg = (length, diameter) => ({ length, diameter });
  return {
    base: seg(PIPES.BASE_LENGTH, PIPES.BASE_DIAMETER),
    risers: Array.from({ length: BUILDING.FLOORS }, () =>
      seg(PIPES.RISER_LENGTH_PER_FLOOR, PIPES.RISER_DIAMETER)
    ),
    floors: Array.from({ length: BUILDING.FLOORS }, () =>
      Array.from({ length: BUILDING.COLUMNS - 1 }, () =>
        seg(PIPES.FLOOR_LENGTH_PER_COLUMN, PIPES.FLOOR_DIAMETER)
      )
    ),
    branches: Array.from({ length: BUILDING.FLOORS * BUILDING.COLUMNS }, () =>
      seg(PIPES.BRANCH_LENGTH, PIPES.BRANCH_DIAMETER)
    ),
  };
};

/** Default system parameters. */
export const createDefaultParams = () => ({
  tOutdoor: OUTDOOR.DEFAULT,
  /** 'fixed' | 'curve' */
  supplyMode: 'fixed',
  tSupplyFixed: HEAT_PUMP.DEFAULT_SUPPLY,
  curveSlope: HEAT_PUMP.CURVE_SLOPE_DEFAULT,
  curveOffset: HEAT_PUMP.CURVE_OFFSET_DEFAULT,
  /** Pump head setting [kPa] */
  pumpHead: PUMP.DEFAULT_HEAD_KPA,
  /** Balancing/commissioning mode: all TRVs forced fully open */
  balancingMode: false,
  airChangeRate: DEFAULT_ACH,
  /** Full per-segment pipe-network geometry. Defaults to the PIPES table. */
  pipes: createDefaultPipes(),
});
